import React, { useEffect, useState, useCallback } from 'react';
import { Download, CheckCircle2, Loader2, Trash2 } from 'lucide-react';
import toast from 'react-hot-toast';
import {
  hasOffline,
  saveOffline,
  removeOffline,
} from '../utils/offlineStore';

/**
 * DownloadButton — toggles offline availability for an individual
 * audiobook chapter, cinematic episode, or standalone audio item.
 * Three visual states:
 *   • not-downloaded (idle) → "Download" pill
 *   • downloading            → spinner + %
 *   • downloaded             → green check, click to remove
 *
 * Props:
 *   id    — unique content id (book_id / episode_id / chapter_id)
 *   url   — relative or absolute media URL (`/uploads/...` etc.)
 *   title — display name for toast + metadata
 *   meta  — any extra metadata (cover_image, duration, series_id) the
 *           caller wants surfaced on the "Downloads" screen.
 *   size  — optional, compact or full visual variant.
 */
const DownloadButton = ({ id, url, title, meta = {}, size = 'compact', iconOnlyOnMobile = false }) => {
  const [state, setState] = useState('idle'); // idle | saving | saved
  const [pct, setPct] = useState(0);

  const refresh = useCallback(async () => {
    const ok = await hasOffline(id);
    setState(ok ? 'saved' : 'idle');
  }, [id]);

  useEffect(() => {
    if (!id) return;
    refresh();
  }, [id, refresh]);

  const onDownload = async (e) => {
    e?.preventDefault?.();
    e?.stopPropagation?.();
    if (!id || !url) {
      toast.error('This item has no downloadable media');
      return;
    }
    if (state !== 'idle') return;
    setState('saving');
    setPct(0);
    const tid = toast.loading(`Saving "${title}" for offline…`);
    try {
      await saveOffline(id, url, { ...meta, title }, ({ pct: p }) => {
        if (p != null) setPct(p);
      });
      setState('saved');
      toast.success(`✓ "${title}" saved for offline`, { id: tid });
    } catch (err) {
      setState('idle');
      toast.error(`Download failed: ${err.message}`, { id: tid });
    }
  };

  const onRemove = async (e) => {
    e?.preventDefault?.();
    e?.stopPropagation?.();
    if (state !== 'saved') return;
    if (!window.confirm(`Remove "${title}" from offline storage?`)) return;
    await removeOffline(id);
    setState('idle');
    setPct(0);
    toast.success('Removed from offline');
  };

  const testIdBase = `download-btn-${id}`;
  const compact = size === 'compact';

  if (state === 'saving') {
    return (
      <button
        type="button"
        disabled
        data-testid={`${testIdBase}-saving`}
        className={`inline-flex items-center gap-1.5 font-semibold rounded-full cursor-wait select-none ${
          compact ? 'text-[11px] px-2.5 py-1' : 'text-xs px-3 py-1.5'
        } bg-indigo-500/15 text-indigo-200 border border-indigo-500/40`}
      >
        <Loader2 className={`${compact ? 'w-3 h-3' : 'w-3.5 h-3.5'} animate-spin`} />
        <span className={iconOnlyOnMobile ? 'hidden sm:inline' : ''}>
          {pct ? `${pct}%` : 'Saving…'}
        </span>
      </button>
    );
  }

  if (state === 'saved') {
    return (
      <button
        type="button"
        onClick={onRemove}
        data-testid={`${testIdBase}-remove`}
        title="Saved for offline — click to remove"
        className={`group inline-flex items-center gap-1.5 font-semibold rounded-full transition-colors select-none ${
          compact ? 'text-[11px] px-2.5 py-1' : 'text-xs px-3 py-1.5'
        } bg-emerald-500/15 text-emerald-200 border border-emerald-500/40 hover:bg-rose-500/15 hover:text-rose-200 hover:border-rose-500/40`}
      >
        <CheckCircle2 className={`${compact ? 'w-3 h-3' : 'w-3.5 h-3.5'} group-hover:hidden`} />
        <Trash2 className={`${compact ? 'w-3 h-3' : 'w-3.5 h-3.5'} hidden group-hover:block`} />
        <span className={`group-hover:hidden ${iconOnlyOnMobile ? 'hidden sm:inline' : ''}`}>Offline</span>
        <span className={`hidden group-hover:inline ${iconOnlyOnMobile ? 'sm:group-hover:inline' : ''}`}>Remove</span>
      </button>
    );
  }

  return (
    <button
      type="button"
      onClick={onDownload}
      data-testid={`${testIdBase}-idle`}
      title="Download for offline listening"
      className={`inline-flex items-center gap-1.5 font-semibold rounded-full transition-colors select-none ${
        compact ? 'text-[11px] px-2.5 py-1' : 'text-xs px-3 py-1.5'
      } bg-slate-800 text-slate-300 border border-slate-700 hover:bg-indigo-500/20 hover:text-indigo-200 hover:border-indigo-500/40`}
    >
      <Download className={`${compact ? 'w-3 h-3' : 'w-3.5 h-3.5'}`} />
      <span className={iconOnlyOnMobile ? 'hidden sm:inline' : ''}>Download</span>
    </button>
  );
};

export default DownloadButton;
